import React, { useEffect, useState } from 'react';
import axios from 'axios';
import { Sparkles, Cake, Heart, ShoppingBag, Check, Plus } from 'lucide-react';
import { toast } from 'sonner';

const API = `${process.env.REACT_APP_BACKEND_URL}/api/cliente/experiencia`;
const INTERESES = ['Moda', 'Tecnología', 'Belleza', 'Hogar', 'Deportes', 'Mascotas', 'Niños', 'Libros', 'Videojuegos', 'Fitness', 'Cocina', 'Viajes'];
const TIENDAS = ['Amazon', 'SHEIN', 'Temu', 'eBay', 'Walmart', 'Nike', 'Apple', 'Sephora', 'Target', 'Best Buy'];
const PROVINCIAS = ['San José', 'Alajuela', 'Cartago', 'Heredia', 'Guanacaste', 'Puntarenas', 'Limón'];
const VACIO = { fecha_nacimiento: '', genero: '', provincia: '', intereses: [], tiendas_favoritas: [], talla_ropa: '', talla_zapato: '', notas: '', acepta_promociones: true };

const Chips = ({ opciones, value, onChange, testPrefix }) => {
  const [extra, setExtra] = useState('');
  const toggle = (o) => onChange(value.includes(o) ? value.filter((v) => v !== o) : [...value, o]);
  const todas = [...new Set([...opciones, ...value])];
  return (
    <div className="flex flex-wrap gap-2">
      {todas.map((o) => (
        <button type="button" key={o} data-testid={`${testPrefix}-chip-${o}`} onClick={() => toggle(o)}
          className={`px-3 py-1.5 rounded-full text-sm border transition-colors flex items-center gap-1 ${value.includes(o) ? 'bg-[#194D3D] text-white border-[#194D3D]' : 'bg-white text-gray-600 border-gray-200 hover:border-[#194D3D]'}`}>
          {value.includes(o) && <Check size={13} />} {o}
        </button>
      ))}
      <span className="flex items-center border border-dashed rounded-full pl-3 pr-1">
        <input data-testid={`${testPrefix}-custom-input`} value={extra} onChange={(e) => setExtra(e.target.value)} placeholder="Otro..."
          className="w-24 text-sm outline-none bg-transparent"
          onKeyDown={(e) => { if (e.key === 'Enter') { e.preventDefault(); if (extra.trim()) { onChange([...value, extra.trim()]); setExtra(''); } } }} />
        <button type="button" data-testid={`${testPrefix}-custom-add`} onClick={() => { if (extra.trim()) { onChange([...value, extra.trim()]); setExtra(''); } }} className="p-1 text-[#194D3D]"><Plus size={14} /></button>
      </span>
    </div>
  );
};

const Field = ({ label, children }) => (
  <label className="block"><span className="block text-sm font-medium text-gray-700 mb-1">{label}</span>{children}</label>
);
const inputCls = 'w-full px-4 py-3 border border-gray-300 rounded-lg focus:ring-2 focus:ring-teal-500 focus:border-transparent outline-none';

export const MiExperiencia = () => {
  const [form, setForm] = useState(VACIO);
  const [saving, setSaving] = useState(false);
  const [updatedAt, setUpdatedAt] = useState(null);
  const headers = { Authorization: `Bearer ${localStorage.getItem('sendhapy_token')}` };

  useEffect(() => {
    axios.get(API, { headers }).then((r) => {
      const d = r.data;
      setForm({ ...VACIO, ...Object.fromEntries(Object.entries(d).map(([k, v]) => [k, v ?? VACIO[k]])) });
      setUpdatedAt(d.updated_at);
    }).catch(() => {});
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  const set = (k) => (e) => setForm({ ...form, [k]: e?.target ? e.target.value : e });

  const save = async (e) => {
    e.preventDefault();
    setSaving(true);
    try {
      const payload = { ...form, fecha_nacimiento: form.fecha_nacimiento || null };
      const { data } = await axios.put(API, payload, { headers });
      setUpdatedAt(data.experiencia.updated_at);
      toast.success(data.message);
    } catch (err) {
      const d = err.response?.data?.detail;
      toast.error(Array.isArray(d) ? d.map((x) => x.msg).join(' ') : d || 'No se pudo guardar');
    } finally {
      setSaving(false);
    }
  };

  return (
    <form onSubmit={save} className="space-y-6" data-testid="mi-experiencia-form">
      <div className="rounded-xl p-6 text-white relative overflow-hidden" style={{ background: 'var(--sendhapy-teal)' }}>
        <Sparkles className="absolute right-6 top-6 opacity-20" size={64} />
        <h3 className="text-xl font-semibold mb-1">Mi Experiencia SendHapy</h3>
        <p className="text-white/80 text-sm max-w-xl">Cuéntanos un poco de ti para sorprenderte en tu cumpleaños y enviarte promos de lo que de verdad te gusta.</p>
      </div>

      <div className="bg-white rounded-xl p-6 shadow-sm grid md:grid-cols-3 gap-4">
        <h4 className="md:col-span-3 font-semibold flex items-center gap-2" style={{ color: 'var(--sendhapy-teal)' }}><Cake size={18} /> Sobre ti</h4>
        <Field label="Fecha de cumpleaños">
          <input data-testid="exp-fecha-nacimiento-input" type="date" max={new Date().toISOString().slice(0, 10)} value={form.fecha_nacimiento} onChange={set('fecha_nacimiento')} className={inputCls} />
        </Field>
        <Field label="Provincia">
          <select data-testid="exp-provincia-select" value={form.provincia} onChange={set('provincia')} className={inputCls}>
            <option value="">Selecciona...</option>{PROVINCIAS.map((p) => <option key={p}>{p}</option>)}
          </select>
        </Field>
        <Field label="Género (opcional)">
          <select data-testid="exp-genero-select" value={form.genero} onChange={set('genero')} className={inputCls}>
            <option value="">Prefiero no decir</option><option>Femenino</option><option>Masculino</option><option>Otro</option>
          </select>
        </Field>
        <Field label="Talla de ropa">
          <input data-testid="exp-talla-ropa-input" placeholder="Ej: M" value={form.talla_ropa} onChange={set('talla_ropa')} className={inputCls} maxLength={20} />
        </Field>
        <Field label="Talla de zapato">
          <input data-testid="exp-talla-zapato-input" placeholder="Ej: 8 US" value={form.talla_zapato} onChange={set('talla_zapato')} className={inputCls} maxLength={20} />
        </Field>
      </div>

      <div className="bg-white rounded-xl p-6 shadow-sm space-y-4">
        <h4 className="font-semibold flex items-center gap-2" style={{ color: 'var(--sendhapy-teal)' }}><Heart size={18} /> ¿Qué te gusta comprar?</h4>
        <Chips opciones={INTERESES} value={form.intereses} onChange={set('intereses')} testPrefix="exp-interes" />
        <h4 className="font-semibold flex items-center gap-2 pt-2" style={{ color: 'var(--sendhapy-teal)' }}><ShoppingBag size={18} /> Tiendas favoritas</h4>
        <Chips opciones={TIENDAS} value={form.tiendas_favoritas} onChange={set('tiendas_favoritas')} testPrefix="exp-tienda" />
        <Field label="Algo más que quieras contarnos">
          <textarea data-testid="exp-notas-input" rows={3} maxLength={500} value={form.notas} onChange={set('notas')} className={inputCls} placeholder="Ej: Me encanta el café y compro regalos para mis sobrinos" />
        </Field>
        <label className="flex items-center gap-3 text-sm text-gray-700">
          <input data-testid="exp-promociones-checkbox" type="checkbox" checked={form.acepta_promociones} onChange={(e) => setForm({ ...form, acepta_promociones: e.target.checked })} className="w-4 h-4 accent-teal-700" />
          Quiero recibir promociones personalizadas
        </label>
      </div>

      <div className="flex items-center gap-4">
        <button data-testid="exp-save-button" disabled={saving} className="px-8 py-3 rounded-full font-semibold transition-transform hover:-translate-y-0.5 disabled:opacity-50"
          style={{ background: 'var(--sendhapy-yellow)', color: 'var(--sendhapy-teal)' }}>
          {saving ? 'Guardando...' : 'Guardar mi experiencia'}
        </button>
        {updatedAt && <span className="text-xs text-gray-400" data-testid="exp-updated-at">Última actualización: {new Date(updatedAt).toLocaleString('es-CR')}</span>}
      </div>
    </form>
  );
};
